import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Paper, Skeleton, Stack, Typography } from "@mui/material";

/**
 * Graceful "unavailable" state rendered when the `menuAnalyticsDashboardEnabled`
 * flag is off but a user navigates directly to the route. Never a blank screen.
 */
export const MenuAnalyticsUnavailable = () => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1.5,
      textAlign: "center",
      p: 4,
    }}
  >
    <LockOutlinedIcon sx={{ fontSize: 48, color: "greyscale.400" }} />
    <Typography variant="h3">Menu Analytics isn't available yet</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
      This dashboard hasn't been enabled for your organization. Reach out to your
      Teamworks administrator if you think you should have access.
    </Typography>
  </Box>
);

/**
 * Empty placeholder shown in a tab panel before the analytics endpoints exist
 * (NUT-2026 / NUT-2027 replace this with the real summary cards + table).
 */
export const MenuAnalyticsEmptyState = ({ label }: { label: string }) => (
  <Box
    sx={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      textAlign: "center",
      p: 4,
      minHeight: 240,
    }}
  >
    <AssessmentOutlinedIcon sx={{ fontSize: 40, color: "greyscale.400" }} />
    <Typography variant="h3">{label} analytics coming soon</Typography>
    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420 }}>
      We're building out this view. Data will appear here once it's ready.
    </Typography>
  </Box>
);

/**
 * Full-screen skeleton/shimmer scaffold for the dashboard: five summary stat
 * cards above a table. Not shown while there is no data to load (NUT-2024);
 * exported so NUT-2026/2027 can render it during their initial fetch.
 */
export const MenuAnalyticsSkeleton = () => (
  <Stack spacing={2} sx={{ flex: 1 }}>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 2 }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height={36} />
        </Paper>
      ))}
    </Box>
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Skeleton variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={index} variant="text" height={32} />
      ))}
    </Paper>
  </Stack>
);
